import getNestedValue from './getNestedValue';
import { getLevelIndex } from '@/utils/constants/organization-matrix';

interface OrgNode {
  id: number;
  level_key?: string | null;
  children?: OrgNode[];
}

// Build a flat map of nodeId → level_key from an org chart tree.
export function buildNodeLevelMap(nodes: OrgNode[]): Map<number, string> {
  const map = new Map<number, string>();
  const walk = (list: OrgNode[]) => {
    for (const node of list) {
      if (node.level_key) map.set(node.id, node.level_key);
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return map;
}

// Sort rows by org-chart hierarchy level (Direktur first, Staff last).
// Rows at the same level are then sorted alphabetically by jabatan name.
// Unknown nodes sink to the bottom.
export function sortByOrgLevel<T>(
  rows: T[],
  getNodeId: (row: T) => number | null | undefined,
  levelMap: Map<number, string>,
  getName: (row: T) => string,
  order: 'asc' | 'desc' = 'asc',
): T[] {
  const dir = order === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const idA = getNodeId(a);
    const idB = getNodeId(b);
    const levelA = idA != null ? getLevelIndex(levelMap.get(idA)) : -1;
    const levelB = idB != null ? getLevelIndex(levelMap.get(idB)) : -1;

    // Unknown nodes (index -1) sink to the bottom regardless of direction
    if (levelA === -1 && levelB === -1)
      return getName(a).localeCompare(getName(b), undefined, { sensitivity: 'base' }) * dir;
    if (levelA === -1) return 1;
    if (levelB === -1) return -1;

    if (levelA !== levelB) return (levelA - levelB) * dir;
    return getName(a).localeCompare(getName(b), undefined, { sensitivity: 'base' }) * dir;
  });
}

export interface SortConfig {
  orderBy?: string;
  order?: 'asc' | 'desc';
}

// Per-column value extractors override the default getNestedValue lookup.
// Needed for array columns (e.g. Punishment `sanctions`, Train `culture_values`)
// and computed columns (e.g. SOP rows built from `_jobDesc` / `_sop`).
// Keyed by column `name`.
export type Accessors<T> = Record<string, (row: T) => unknown>;

// Normalize a raw cell value to a comparable form. Rich-text/HTML fields are
// stripped to plain text so they sort by their visible content.
const normalize = (value: unknown): string | number | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isNaN(value) ? null : value;

  const text = String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (text === '') return null;
  return text;
};

// Compare two non-blank normalized values. Numeric strings compare numerically,
// otherwise case-insensitive locale compare. Blank handling is done by the
// caller so blanks can always sink regardless of sort direction.
export const compareValues = (left: string | number, right: string | number): number => {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  const leftNum = Number(left);
  const rightNum = Number(right);
  if (!Number.isNaN(leftNum) && !Number.isNaN(rightNum)) {
    return leftNum - rightNum;
  }

  return String(left).localeCompare(String(right), undefined, { sensitivity: 'base' });
};

// Stable client-side sort. Returns a new array; original order is preserved on
// ties and when no orderBy is set. Blanks always sink to the bottom regardless
// of sort direction.
export function sortRows<T>(rows: T[], { orderBy, order = 'asc' }: SortConfig, accessors: Accessors<T> = {}): T[] {
  if (!orderBy) return rows;

  const dir = order === 'desc' ? -1 : 1;
  const get = (row: T) => normalize(accessors[orderBy] ? accessors[orderBy](row) : getNestedValue(row, orderBy));

  return rows
    .map((row, index) => [row, index, get(row)] as const)
    .sort((a, b) => {
      const left = a[2];
      const right = b[2];

      // Blanks sink to the bottom in both directions.
      if (left === null && right === null) return a[1] - b[1];
      if (left === null) return 1;
      if (right === null) return -1;

      const primary = compareValues(left, right) * dir;
      return primary !== 0 ? primary : a[1] - b[1];
    })
    .map(([row]) => row);
}
